import React, { useState, useEffect, useRef } from "react";
import "../i18n";
import LanguageSelector from "./LanguageSelector";
import { useTranslation } from "react-i18next";
import "./ChatBox.css";
import "./ChatBox_responsive.css"; // NEW: Responsive mobile-first CSS

const API_TOKEN = process.env.REACT_APP_API_TOKEN || "YOURBIGRANDOMTOKEN";
const API_BASE = process.env.REACT_APP_API_URL;
const FALLBACK_QUESTIONS = [
  "What award covers a nurse in VIC?",
  "Do casuals get paid rest breaks in NSW?",
  "What should I do if I'm being bullied at work?",
  "Does my employer have to provide PPE in NSW?",
  "How does the right to strike apply in Australia?",
  "What leave entitlements do parents have under Fair Work?"
];

const Disclaimer = ({ children }) => (
  <div style={{
    marginTop: "6px",
    marginBottom: "6px",
    padding: "6px 8px",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeeba",
    borderRadius: "6px",
    fontSize: "11px",
    color: "#856404"
  }}>
    ⚠️ {children}
  </div>
);

const SuggestedQuestions = ({ onQuestionClick, visible, onToggle }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && suggestions.length === 0) {
      loadSuggestions();
    }
  }, [visible, suggestions.length]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/diag/popular`);
      if (res.ok) {
        const data = await res.json();
        const popular = data.popular || [];
        if (popular.length > 0) {
          setSuggestions(popular.map(p => p.query));
        } else {
          setSuggestions(FALLBACK_QUESTIONS);
        }
      } else {
        setSuggestions(FALLBACK_QUESTIONS);
      }
    } catch (e) {
      console.error("Failed to load suggestions:", e);
      setSuggestions(FALLBACK_QUESTIONS);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      marginBottom: "16px",
      backgroundColor: "#f8f9fa"
    }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          padding: "12px 16px",
          backgroundColor: "transparent",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "600",
          color: "#495057",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        Popular Questions
        <span style={{ transform: visible ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
          ▼
        </span>
      </button>

      {visible && (
        <div style={{ padding: "0 16px 16px 16px" }}>
          {loading ? (
            <div style={{ color: "#6c757d", fontSize: "12px" }}>Loading suggestions...</div>
          ) : (
            <div style={{ display: "grid", gap: "8px" }}>
              {suggestions.slice(0, 6).map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => onQuestionClick(question)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    backgroundColor: "transparent",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    padding: "6px 8px",
                    margin: "2px 0",
                    fontSize: "12px",
                    color: "#495057",
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#e9ecef";
                    e.target.style.borderColor = "#adb5bd";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.borderColor = "#dee2e6";
                  }}
                >
                  {question}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RelevantSections = ({ sections }) => {
  if (!sections || sections.length === 0) return null;
  
  return (
    <div style={{
      backgroundColor: "#fff3e0",
      border: "1px solid #ffb74d",
      borderRadius: "6px",
      padding: "8px 12px",
      marginTop: "8px",
      fontSize: "12px"
    }}>
      <div style={{ fontWeight: "600", color: "#f57c00", marginBottom: "4px" }}>
        Relevant Legal Sections
      </div>
      {sections.map((section, idx) => (
        <div key={idx} style={{ color: "#e65100", marginBottom: "2px" }}>
          • {section}
        </div>
      ))}
    </div>
  );
};

const NextSteps = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  
  return (
    <div style={{
      backgroundColor: "#e8f5e8",
      border: "1px solid #81c784",
      borderRadius: "6px",
      padding: "8px 12px",
      marginTop: "8px",
      fontSize: "12px"
    }}>
      <div style={{ fontWeight: "600", color: "#388e3c", marginBottom: "4px" }}>
        Suggested Next Steps
      </div>
      {steps.map((step, idx) => (
        <div key={idx} style={{ color: "#2e7d32", marginBottom: "2px" }}>
          {idx + 1}. {step}
        </div>
      ))}
    </div>
  );
};

export default function ChatBox() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSources, setShowSources] = useState({}); // NEW: Track which sources are expanded
  const [retryCount, setRetryCount] = useState(0);
  const messagesEndRef = useRef(null);
  const { t, i18n } = useTranslation();

  async function sendMessage(optionalText) {
    const text = optionalText || input.trim();
    if (!text) return;

    setMessages((prev) => [...prev, { role: "user", content: text, ts: new Date() }]);
    setInput("");
    setLoading(true);

    try {
      const currentLang = i18n?.language || "auto";

      const res = await fetch(`${API_BASE}/answer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${API_TOKEN}`,
        },
        body: JSON.stringify({ query: text, language: currentLang }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.answer,
            ts: new Date(),
            data,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "⚠️ Error: Failed to get response",
            isError: true,
            ts: new Date(),
          },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "⚠️ Error: Network issue",
          isError: true,
          ts: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function formatTime(ts) {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit"
    }).format(ts);
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    });
  }

  return (
    <div className="chat-container">
      
      {/* Header */}
      <div className="chat-header">
        <h1 className="chat-title">Australian Workplace Rights Assistant</h1>
        <p className="chat-subtitle">
          {t("assistant_description")}
        </p>
      </div>

      {/* Testing Banner */}
      <div className="testing-banner">
        <span className="testing-banner-text">
          This is a testing version — answers are for evaluation only, not legal advice.
        </span>
      </div>

      {/* Controls */}
      <div className="chat-controls">
        <div className="language-selector">
          <LanguageSelector />
        </div>
        <button 
          className="clear-button"
          onClick={async () => {
            setMessages([]);
            try {
              await fetch(`${API_BASE}/my-data`, {
                method: 'DELETE',
                headers: { "Authorization": `Bearer ${API_TOKEN}` }
              });
            } catch (e) {
              console.log("Server memory clear failed, but UI cleared");
            }
          }}
        >
          Clear Chat
        </button>
      </div>

      {retryCount > 0 && (
        <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "8px", textAlign: "center" }}>
          Retrying... (attempt {retryCount + 1})
        </div>
      )}

      {/* Messages Area */}
      <div className="chat-messages">
        {messages.map((msg, idx) => (
          <div key={idx} className="message">
            
            {/* USER MESSAGE */}
            {msg.role === "user" && (
              <div className="user-message">
                <div className="user-bubble">
                  <p className="user-text">{msg.content}</p>
                  <div className="user-time">{formatTime(msg.ts)}</div>
                </div>
              </div>
            )}

            {/* BOT MESSAGE */}
            {msg.role === "assistant" && (
              <div className="bot-message">
                <div className="bot-bubble">
                  
                  {/* Badge - removed as per user request */}
                  {/* <div className="bot-badge">Legal Assistant</div> */}
                  
                  {/* Main Text */}
                  <div 
                    className="bot-text"
                    dangerouslySetInnerHTML={{ __html: msg.content }}
                  />

                  {/* Disclaimer */}
                  <div className="disclaimer">
                    <span className="disclaimer-text">
                      General information only, not legal advice.
                    </span>
                  </div>

                  {/* Sources */}
                  {Array.isArray(msg.data?.sources_list) && msg.data.sources_list.length > 0 && (
                    <div className="sources-container">
                      <button 
                        className="sources-toggle"
                        onClick={() => {
                          const newShow = {...showSources};
                          newShow[idx] = !newShow[idx];
                          setShowSources(newShow);
                        }}
                      >
                        {showSources[idx] ? 'Hide sources' : `${msg.data.sources_list.length} sources`}
                      </button>
                      
                      {showSources[idx] && (
                        <div className="sources-list">
                          {/* International treaty disclaimer */}
                          {msg.data.sources_list.every(src => {
                            const label = typeof src === "string" ? src.toLowerCase() : (src.label || "").toLowerCase();
                            return label.includes("international-ilo") ||
                                   label.includes("international-un") ||
                                   label.includes("ilo") ||
                                   label.includes("iccpr") ||
                                   label.includes("cedaw");
                          }) && (
                            <Disclaimer>
                              All of these sources are from international treaties. Remember: treaties guide
                              Australia's obligations but may not be directly enforceable in domestic law.
                            </Disclaimer>
                          )}

                          {msg.data.sources_list.map((source, i) => {
                            const label = typeof source === "string" ? source : (source.label || "");
                            const href = typeof source === "string" ? undefined : source.href;
                            return (
                              <div key={i} className="source-item">
                                {href ? (
                                  <a href={href} target="_blank" rel="noreferrer">{label}</a>
                                ) : (
                                  label
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Relevant Sections */}
                  <RelevantSections sections={msg.data?.relevant_sections} />
                  
                  {/* Next Steps */}
                  <NextSteps steps={msg.data?.next_steps} />

                  {/* Actions */}
                  <div className="message-actions">
                    <button 
                      className="action-btn"
                      onClick={() => copyToClipboard(msg.content.replace(/<[^>]*>/g, ''))}
                      title="Copy response"
                    >
                      <span className="action-btn-icon">📋</span>
                      <span>Copy</span>
                    </button>
                  </div>

                </div>
              </div>
            )}

          </div>
        ))}

        {/* Loading State */}
        {loading && (
          <div className="loading-message">
            <div className="loading-bubble">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="loading-text">
                {retryCount > 0 ? "Retrying..." : "Searching legal sources..."}
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="input-wrapper">
          <textarea
            className="chat-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("input_placeholder")}
            rows={2}
            disabled={loading}
          />
          <button 
            className="send-button"
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
          >
            {loading ? 'Sending...' : 'Send'}
          </button>
        </div>
        <div className="input-helper">
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>

      {/* Suggested Questions - Show only when no messages */}
      {messages.length === 0 && (
        <div style={{ padding: "0 16px 16px 16px" }}>
          <SuggestedQuestions 
            visible={showSuggestions} 
            onQuestionClick={(q) => {
              sendMessage(q);
              setShowSuggestions(false);
            }}
            onToggle={() => setShowSuggestions(!showSuggestions)}
          />
        </div>
      )}

      {/* Feedback & Privacy */}
      <div style={{ 
        padding: "12px 16px", 
        textAlign: "center", 
        borderTop: "1px solid #e5e7eb",
        background: "#f9fafb"
      }}>
        <button
          onClick={() =>
            window.location.href = "mailto:eamon@insurgency.com.au?subject=Chatbot Feedback"
          }
          style={{
            fontSize: "12px",
            color: "#0d6efd",
            background: "transparent",
            border: "none",
            textDecoration: "underline",
            cursor: "pointer",
            marginRight: "12px"
          }}
        >
          💬 Send Feedback
        </button>

        <button
          onClick={() => alert(`Privacy Information:

- We temporarily store your questions to maintain conversation context
- Questions are processed by OpenAI's API and retained by them for up to 30 days for safety monitoring  
- Your data is not used to train AI models
- Click "Clear Chat" to remove data from our system`)}
          style={{
            fontSize: "12px",
            padding: "4px 8px",
            backgroundColor: "transparent",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            color: "#6c757d",
            cursor: "pointer"
          }}
        >
          Privacy
        </button>
      </div>

    </div>
  );
}
